import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { TableSortLabel } from "@mui/material";
import { Box, Card, Stack, Typography } from "@curves/ui-base";
import { ProductInfo, Table as SharedTable } from "@curves/ui-shared";
import useClubInventoryApi from "@Apis/useClubInventoryApi";
import { PageLayout } from "@Local/Components/Layout";
import { CLUB_INVENTORY_PRODUCT_STOCK_OVERVIEW_ROUTER } from "@Router/routerPath";

const EMPTY_DETAIL = {
	detail: null,
	clubStocks: [],
	totalCount: 0,
};

const SORT_KEYS = {
	SELLABLE: "sellableQty",
	DEFECT: "defectQty",
};

/**
 * 依 orderBy/order 回傳排序後的列表，orderBy 為 null 時保留原始順序
 */
const sortStores = (clubStocks, orderBy, order) => {
	if (!orderBy) return clubStocks;
	const direction = order === "asc" ? 1 : -1;
	return [...clubStocks].sort((a, b) => ((Number(a[orderBy]) || 0) - (Number(b[orderBy]) || 0)) * direction);
};

/**
 * HQ 商品庫存明細（商品角度）
 * - 顯示該商品的摘要（商品資訊、跨店舖彙整數量）
 * - 列出所有已有庫存紀錄之店舖（不分頁、顯示總筆數）
 * - 僅提供檢視功能
 */
const ProductStockDetailPage = () => {
	const params = useParams();
	const productID = Number(params.productID) || 0;
	const variantID = Number(params.variantID) || 0;

	const { postClubInventoryProductStockDetailApi } = useClubInventoryApi();
	const [state, setState] = useState(EMPTY_DETAIL);
	const [orderBy, setOrderBy] = useState(/** @type {string | null} */ (SORT_KEYS.SELLABLE));
	const [order, setOrder] = useState(/** @type {"asc" | "desc"} */ ("asc"));

	/**
	 * 點擊欄位排序：
	 * - 首次點擊預設降冪（多到少）
	 * - 同一欄再次點擊切換 asc/desc
	 */
	const handleRequestSort = useCallback(
		key => {
			if (orderBy === key) {
				setOrder(prev => (prev === "asc" ? "desc" : "asc"));
				return;
			}
			setOrderBy(key);
			setOrder("desc");
		},
		[orderBy],
	);

	const fetchDetail = useCallback(async () => {
		if (!productID) return;
		try {
			const resp = await postClubInventoryProductStockDetailApi({ productID, variantID });
			setState({
				detail: resp?.detail ?? null,
				clubStocks: Array.isArray(resp?.clubStocks) ? resp.clubStocks : [],
				totalCount: resp?.totalCount ?? 0,
			});
		} catch {
			setState(EMPTY_DETAIL);
		}
	}, [postClubInventoryProductStockDetailApi, productID, variantID]);

	useEffect(() => {
		fetchDetail();
	}, [fetchDetail]);

	const { detail, clubStocks, totalCount } = state;

	const sortedStores = useMemo(() => sortStores(clubStocks, orderBy, order), [clubStocks, orderBy, order]);
	const tableHeaderRow = useMemo(
		() => [
			{ cellKey: "clubName", headerLabel: "店鋪名稱", width: 16, minWidth: 220 },
			{
				cellKey: SORT_KEYS.SELLABLE,
				align: "center",
				width: 10,
				minWidth: 160,
				headerLabel: () => (
					<TableSortLabel
						className={`product-stock-sort-label ${orderBy === SORT_KEYS.SELLABLE ? "is-active" : ""}`}
						active={orderBy === SORT_KEYS.SELLABLE}
						direction={orderBy === SORT_KEYS.SELLABLE ? order : "desc"}
						onClick={() => handleRequestSort(SORT_KEYS.SELLABLE)}
					>
						可販售數量
					</TableSortLabel>
				),
			},
			{
				cellKey: SORT_KEYS.DEFECT,
				align: "center",
				width: 10,
				minWidth: 160,
				headerLabel: () => (
					<TableSortLabel
						className={`product-stock-sort-label ${orderBy === SORT_KEYS.DEFECT ? "is-active" : ""}`}
						active={orderBy === SORT_KEYS.DEFECT}
						direction={orderBy === SORT_KEYS.DEFECT ? order : "desc"}
						onClick={() => handleRequestSort(SORT_KEYS.DEFECT)}
					>
						瑕疵數量
					</TableSortLabel>
				),
			},
		],
		[orderBy, order, handleRequestSort],
	);

	const pageActions = [
		{
			label: "返回總覽",
			variant: "contained",
			as: NavLink,
			to: CLUB_INVENTORY_PRODUCT_STOCK_OVERVIEW_ROUTER,
		},
	];

	return (
		<PageLayout className="product-stock-detail-page">
			<PageLayout.Header title="商品庫存明細" actions={pageActions} />

			{/* 商品摘要：商品資訊、跨店舖彙整的可販售/瑕疵數量 */}
			<Card className="p-3 mb-3 stock-summary-card">
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Box className="stock-summary-product">
						<ProductInfo
							className="stock-summary-product-info"
							imageSize={120}
							showEditIcon={false}
							onEdit={() => {}}
							product={{
								sku: detail?.sku,
								name: detail?.productName,
								spec: detail?.variantName,
								category: detail?.saleCategoryName,
							}}
						/>
					</Box>
					<Box className="stock-summary-meta">
						<Box className="stock-summary-meta-item">
							<Typography variant="body2" className="stock-summary-meta-label">
								可販售數量
							</Typography>
							<Typography variant="body1" className="stock-summary-meta-value">
								{detail?.sellableQty ?? "—"}
							</Typography>
						</Box>
						<Box className="stock-summary-meta-item">
							<Typography variant="body2" className="stock-summary-meta-label">
								瑕疵數量
							</Typography>
							<Typography variant="body1" className="stock-summary-meta-value">
								{detail?.defectQty ?? "—"}
							</Typography>
						</Box>
					</Box>
				</Stack>
			</Card>

			{/* 各店舖庫存列表（不分頁） */}
			<Stack direction="row" justifyContent="space-between" alignItems="center" className="mb-2">
				<Typography variant="h6" className="mb-0 font-weight-bold">
					各店舖庫存
				</Typography>
				<Typography variant="body2" className="mb-0">
					總筆數：{totalCount}
				</Typography>
			</Stack>

			<SharedTable className="mb-3" rowKey="clubID" cardProps={{ className: "p-3 mb-3" }}>
				<SharedTable.Header headerRow={tableHeaderRow} />
				<SharedTable.Body rowsData={sortedStores} headerRow={tableHeaderRow} />
			</SharedTable>
		</PageLayout>
	);
};

export default ProductStockDetailPage;
